import { ComponentName, OverviewKpi, OverviewKpiTitle } from '@models/Products';
import { NoResult } from '@pages/NoResult';
import { noResultForProductOverviewTab } from '@pages/product-analyzer/Product';
import { useGeneratePageElementViewParam } from '@utils/page-analytics/model/PageElementViewedTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { useInViewTracking } from '@utils/hook/tracking/UseInViewTracking';
import { PageName, PageType, SubSection } from '@utils/pageConfig/Types';
import { isEmpty } from 'lodash';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { WithTile, WithTiles } from '@components/design';
import { RI_PRODUCT_SELLING_PRODUCTS_DESCRIPTION, RI_PRODUCT_SELLING_PRODUCTS_TITLE } from '@pages/Utilities';

interface Props {
  overviewKpi: OverviewKpi;
  isLoading: boolean;
  subSectionForAmplitudeTracking: SubSection;
}

const PRODUCTS_KPI: string[] = ['marketTotal', 'retailerTotal', 'marketNew', 'retailerNew'] as const;

const renderProductKpiTiles = (
  overviewKpi: OverviewKpi,
  ProductKpiTilesComponent: (node?: Element | null | undefined) => void,
  isLoading: boolean,
) => {
  if (noResultForProductOverviewTab(overviewKpi) && !isLoading) {
    return <div ref={ProductKpiTilesComponent}>{NoResult()}</div>;
  }

  return (
    <div ref={ProductKpiTilesComponent} className="retail-market-performance-tiles">
      <WithTiles>
        {PRODUCTS_KPI.map((key: string) => (
          <WithTile testId={`product-kpi-tile-${key}`} key={key} isLoading={isLoading}>
            <div className="flex-column items-center h-20">
              <h4 className="mb-1 font-normal" aria-describedby="product-kpi-info">
                {OverviewKpiTitle[key]}
              </h4>
              <p className="text-2xl font-bold" id="product-kpi-info">
                {overviewKpi[key]}
              </p>
            </div>
          </WithTile>
        ))}
      </WithTiles>
    </div>
  );
};

const ProductKpiTilesContainer: React.FC<Props> = ({ overviewKpi, isLoading, subSectionForAmplitudeTracking }) => {
  const pageElementViewParam = useGeneratePageElementViewParam();
  const pagesConfig = usePageConfig();
  const customPageElementViewParam = () => ({
    ...pageElementViewParam,
    pageType: PageType.RI_PRODUCT,
    pageName: PageName.RI_PRODUCT,
    componentName: ComponentName.PRODUCT_KPI_CARD_CONTAINER,
    elementType: ElementType.TILES,
    hasData: !isEmpty(overviewKpi?.items),
    access: pagesConfig.access,
    subSection: subSectionForAmplitudeTracking,
  });
  const { ref: ProductKpiTilesComponent } = useInViewTracking(customPageElementViewParam);

  return (
    <div data-test-id="product-kpi-tiles-container">
      <h3 className="text-xl font-normal mb-5 mt-1" aria-describedby="product-selling-description">
        {RI_PRODUCT_SELLING_PRODUCTS_TITLE}
      </h3>
      <p className="mb-4" id="product-selling-description">
        {RI_PRODUCT_SELLING_PRODUCTS_DESCRIPTION}
      </p>

      {renderProductKpiTiles(overviewKpi, ProductKpiTilesComponent, isLoading)}
    </div>
  );
};
export default ProductKpiTilesContainer;
